import React from 'react';
import * as Styles from './financing-plan.less';
import '@servicetitan/design-system/dist/system.css';
import { Card, BodyText, Icon, Button, Stack, Eyebrow } from '@servicetitan/design-system';
import { CalculatedProgram } from 'src/api';

interface FinancingPlanProps {
    plan: CalculatedProgram;
    isSelected: boolean;
    loanAmount: number;
    selectPlan(planNumber: string): void;
    unSelectPlan(planNumber: string): void;
    hideAction?: boolean;
}

export const FinancingPlan: React.FC<FinancingPlanProps> = ({
    plan,
    hideAction,
    loanAmount,
    selectPlan,
    unSelectPlan,
    isSelected,
}) => {
    const isDisabled = () => {
        const needToBeDisabled = !plan.eligibleForApplication;
        if (needToBeDisabled) {
            unSelectPlan(plan.planNumber!);
        }
        return needToBeDisabled;
    };

    const renderPromoteCell = () =>
        plan.promotionalTerms > 0 ? (
            <React.Fragment>
                <Stack.Item className={Styles.promoCell}>
                    <Stack direction="column">
                        <Stack.Item className="m-b-1">
                            <Eyebrow size="large" className={Styles.promoText}>
                                {plan.promotionalTerms!} MONTHS PROMO
                            </Eyebrow>
                        </Stack.Item>
                        <Stack.Item>
                            <Stack>
                                <Stack.Item className={Styles.subCell}>
                                    <BodyText subdued size="small">
                                        Monthly Payment
                                    </BodyText>
                                    <BodyText size="medium" className={Styles.promoText}>
                                        ${plan.promotionalMonthlyPayment!.toFixed(2)}
                                    </BodyText>
                                </Stack.Item>
                                <Stack.Item className={Styles.subCell}>
                                    <BodyText subdued size="small">
                                        Max Interest
                                    </BodyText>
                                    <BodyText size="medium">{plan.defaultRate!}</BodyText>
                                </Stack.Item>
                            </Stack>
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
                <Stack.Item className={Styles.divider}></Stack.Item>
            </React.Fragment>
        ) : null;

    const renderRegularCell = () => (
        <React.Fragment>
            <Stack.Item className={Styles.regularCell}>
                <Stack direction="column">
                    <Stack.Item className="m-b-1">
                        <Eyebrow size="large">REGULAR PAYMENTS</Eyebrow>
                    </Stack.Item>
                    <Stack.Item>
                        <Stack>
                            <Stack.Item className={Styles.subCell}>
                                <BodyText subdued size="small">
                                    Monthly Payment
                                </BodyText>
                                <BodyText size="medium">${plan.defaultMonthlyPayment!}</BodyText>
                            </Stack.Item>
                            <Stack.Item className={Styles.subCell}>
                                <BodyText subdued size="small">
                                    Max Interest
                                </BodyText>
                                <BodyText size="medium">{plan.defaultRate!}</BodyText>
                            </Stack.Item>
                            <Stack.Item className={Styles.subCell}>
                                <BodyText subdued size="small">
                                    Terms
                                </BodyText>
                                <BodyText size="medium">{plan.defaultTerms!}</BodyText>
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>
                </Stack>
            </Stack.Item>
            <Stack.Item className={Styles.divider}></Stack.Item>
        </React.Fragment>
    );

    const renderPlanInfoCell = () => (
        <Stack.Item className={Styles.planInfoCell}>
            <Stack direction="column">
                <Stack.Item>
                    <Stack>
                        <Stack.Item className={Styles.subCell}>
                            <BodyText subdued size="small">
                                Min Loan Amt
                            </BodyText>
                            <BodyText
                                size="medium"
                                className={isDisabled() ? Styles.alertAmount : ''}
                            >
                                ${plan.minLoanAmount!}
                            </BodyText>
                        </Stack.Item>
                        <Stack.Item className={Styles.subCell}>
                            <BodyText subdued size="small">
                                Plan
                            </BodyText>
                            <BodyText size="medium">{plan.planNumber!}</BodyText>
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
            </Stack>
        </Stack.Item>
    );
    return (
        <Card
            className={isSelected ? Styles.selectedPlanCard : Styles.planCard}
            disabled={isDisabled()}
        >
            {isDisabled() && (
                <span className={Styles.alert}>
                    <Icon name="info" color="#E63717" size={32}>
                        info
                    </Icon>
                </span>
            )}
            <Card.Section className={Styles.planCardSection}>
                <Stack direction="column">
                    <Stack.Item>
                        <BodyText size="medium">{plan.description!}</BodyText>
                    </Stack.Item>
                    <Stack.Item>
                        <Stack className="m-t-1">
                            {renderPromoteCell()}
                            {renderRegularCell()}
                            {renderPlanInfoCell()}
                        </Stack>
                    </Stack.Item>
                </Stack>
            </Card.Section>
            {hideAction ? null : (
                <Card.Section className={Styles.planCardSection}>
                    <Stack justifyContent="center" className={Styles.footerButton}>
                        <Stack.Item>
                            <Button
                                fill={isSelected ? 'solid' : 'outline'}
                                className={Styles.selectButton}
                                primary={!isDisabled()}
                                disabled={isDisabled()}
                                onClick={() =>
                                    isSelected
                                        ? unSelectPlan(plan.planNumber!)
                                        : selectPlan(plan.planNumber!)
                                }
                            >
                                {isSelected ? 'Unselect' : 'Select'}
                            </Button>
                        </Stack.Item>
                    </Stack>
                </Card.Section>
            )}
        </Card>
    );
};

FinancingPlan.defaultProps = {
    hideAction: false,
};
